"use client";

import { useEffect, useState } from "react";
import type { DeepResearchRunCounts, DeepResearchStage } from "@/app/lib/types";

interface DeepResearchProgressProps {
  startedAt: number;
  searches: number;
  corpusLookups: number;
  costUsd?: number;
  runCounts?: DeepResearchRunCounts | null;
  expectedDurationSeconds?: number;
  cancelling?: boolean;
  onCancel: () => void;
  lane?: "full" | "aplus" | null;
  stages?: DeepResearchStage[] | null;
  stage?: number | null;
  stagesDone?: number[] | null;
  stageStartedAt?: string | null;
  detail?: string | null;
  earlyLook?: string | null;
  expectedFinishAt?: string | null;
  attachmentsIgnored?: boolean;
}

function duration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return minutes ? `${minutes}m ${remainder}s` : `${remainder}s`;
}

export default function DeepResearchProgress({ startedAt, searches, corpusLookups, costUsd, runCounts, expectedDurationSeconds, cancelling, onCancel, lane, stages, stage, stagesDone, stageStartedAt, detail, earlyLook, expectedFinishAt, attachmentsIgnored }: DeepResearchProgressProps) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, []);
  const hasStages = stage != null || stages != null;
  const expectedFinishTimestamp = expectedFinishAt ? new Date(expectedFinishAt).getTime() : NaN;
  const expectedAt = Number.isFinite(expectedFinishTimestamp)
    ? expectedFinishTimestamp
    : expectedDurationSeconds && expectedDurationSeconds > 0
      ? startedAt + expectedDurationSeconds * 1_000
      : undefined;
  const titleDurationSeconds = expectedDurationSeconds && expectedDurationSeconds > 0
    ? expectedDurationSeconds
    : expectedAt !== undefined
      ? Math.max(0, Math.round((expectedAt - startedAt) / 1_000))
      : undefined;
  const formatTime = (time: number) => new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" }).format(time);
  const fallbackStageLabels: Record<number, string> = {
    1: "Planning the research", 2: "Researching", 3: "Filling gaps", 4: "Forming a view",
    5: "Writing the report", 6: "Checking facts and dates", 7: "Final read-through",
  };
  const stageRows = stages ?? (lane === "aplus" ? [1, 2, 5, 6, 7] : [1, 2, 3, 4, 5, 6, 7]).map((number) => ({ number, label: fallbackStageLabels[number] }));
  const displayedStage = stage ?? 1;
  const runningElapsed = stageStartedAt ? duration(Math.max(0, Math.floor((now - new Date(stageStartedAt).getTime()) / 1_000))) : "";
  const formatCount = (count: number) => count >= 1_000 ? count.toLocaleString() : String(count);
  return (
    // The running card carries the same viewport-height contract as every
    // other card in the chat surface (ResponseCard, JudgeCard, and the
    // finished DeepResearchCard). Without it this box sized to its content,
    // which both looked wrong and starved the pin-to-top scroll in page.tsx
    // of the full-viewport content it clamps against — a Deep Research run
    // has no provider cards to supply that height. See OPEN.md #142.
    <div style={{ minHeight: "calc(100dvh - var(--bottom-bar-h, 120px) - var(--attachment-bar-h, 0px) - 160px)", flexShrink: 0, padding: "16px", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", background: "var(--surface)", display: "flex", flexDirection: "column", gap: "8px" }}>
      {hasStages ? <>
        <strong style={{ color: "var(--text-secondary)" }}>Deep Research{titleDurationSeconds ? ` - about ${Math.round(titleDurationSeconds / 60)} min` : ""} - started {formatTime(startedAt)} - expected {expectedAt ? formatTime(expectedAt) : "—"}</strong>
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {stageRows.map(({ number, label }) => <div key={number} style={{ color: number === displayedStage ? "var(--text-secondary)" : "var(--text-tertiary)", fontSize: "14px" }}>
            <div>{stagesDone?.includes(number) ? "✓" : number === displayedStage ? "●" : "○"} {label}{number === displayedStage && runningElapsed ? ` · ${runningElapsed}` : ""}</div>
            {number === displayedStage && detail && <div style={{ marginLeft: "18px", color: "var(--text-tertiary)" }}>{detail}</div>}
          </div>)}
        </div>
        {earlyLook && <div style={{ padding: "10px", borderRadius: "var(--radius-sm)", background: "var(--surface-hover, var(--bg))", whiteSpace: "pre-wrap", color: "var(--text-secondary)" }}><strong>Early look - the full report is still being written and checked</strong><div style={{ marginTop: "4px" }}>{earlyLook}</div></div>}
        {attachmentsIgnored && <span style={{ color: "var(--text-tertiary)", fontSize: "14px" }}>Attachments aren't used for Deep Research.</span>}
        {costUsd !== undefined && <span style={{ color: "var(--text-tertiary)", fontSize: "14px" }}>Spent so far ${costUsd.toFixed(2)}</span>}
        {runCounts && <span style={{ color: "var(--text-tertiary)", fontSize: "14px" }}>{formatCount(runCounts.web_searches)} web search{runCounts.web_searches === 1 ? "" : "es"}{runCounts.library_documents_total > 0 && <> · {formatCount(runCounts.library_documents_read)} of {formatCount(runCounts.library_documents_total)} library documents read · {formatCount(runCounts.library_facts)} library fact{runCounts.library_facts === 1 ? "" : "s"}</>}</span>}
        <div style={{ display: "flex", gap: "8px" }}>
          <button type="button" onClick={() => { if (window.confirm("Stop research? What it has spent so far is not refunded.")) onCancel(); }} disabled={cancelling} style={{ padding: "6px 10px", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", background: "transparent", color: "var(--text-secondary)", cursor: cancelling ? "wait" : "pointer" }}>{cancelling ? "Cancelling…" : "Stop research"}</button>
        </div>
      </> : <>
        <strong>Deep Research is running in the background</strong>
        <span style={{ color: "var(--text-secondary)", fontSize: "14px" }}>Elapsed {duration(Math.floor((now - startedAt) / 1000))} · {searches} web search{searches === 1 ? "" : "es"} performed{corpusLookups !== 0 ? ` · ${corpusLookups} corpus lookup${corpusLookups === 1 ? "" : "s"} performed` : ""}{costUsd !== undefined ? ` · Accrued $${costUsd.toFixed(2)}` : ""}</span>
        {attachmentsIgnored && <span style={{ color: "var(--text-tertiary)", fontSize: "14px" }}>Attachments aren't used for Deep Research.</span>}
        {expectedDurationSeconds !== undefined && <span style={{ color: "var(--text-tertiary)", fontSize: "13px" }}>Expected duration: about {duration(expectedDurationSeconds)}</span>}
        <div><button type="button" onClick={() => { if (window.confirm("Stop research? What it has spent so far is not refunded.")) onCancel(); }} disabled={cancelling} style={{ padding: "6px 10px", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", background: "transparent", color: "var(--text)", cursor: cancelling ? "wait" : "pointer" }}>{cancelling ? "Stopping…" : "Stop research"}</button></div>
      </>}
    </div>
  );
}
